import React, { useState, useEffect } from "react";
import { Input, DatePicker, Button, Tooltip, List, Spin, Select } from "antd";
import ModalWrapper from "../../Modal";
import { PlusOutlined } from "@ant-design/icons";
import { Trash2 } from "lucide-react";
import { getRequest, postRequest } from "../../service/index";

const initialAsset = {
    productName: "",
    serialNumber: "",
    price: "",
    assignDate: null,
    description: "",
};

const AssetModal = ({ open, onRequestClose }) => {

    const [assets, setAssets] = useState([{ ...initialAsset, id: 1 }]);
    const [nextId, setNextId] = useState(2);
    const [employees, setEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [showEmployeeList, setShowEmployeeList] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const searchInputRef = React.useRef(null);
    const listRef = React.useRef(null);
    const [inventory, setInventory] = useState([]);
    const [loadingInventory, setLoadingInventory] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        if (open) {
            fetchEmployees();
            setSelectedEmployee(null);
            setEmployeeSearch("");
            fetchInventory();
        }
    }, [open]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                searchInputRef.current &&
                !searchInputRef.current.contains(event.target) &&
                listRef.current &&
                !listRef.current.contains(event.target)
            ) {
                setShowEmployeeList(false);
            }
        }
        if (showEmployeeList) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showEmployeeList]);

    const fetchEmployees = async () => {
        setLoadingEmployees(true);
        try {
            const res = await getRequest("/api/companies/reportto");
            setEmployees(res.employees || []);
        } catch (err) {
            setEmployees([]);
        } finally {
            setLoadingEmployees(false);
        }
    };

    const fetchInventory = async () => {
        setLoadingInventory(true);
        try {
            const res = await getRequest("/api/inventory");
            setInventory(Array.isArray(res) ? res : []);
        } catch (err) {
            setInventory([]);
        } finally {
            setLoadingInventory(false);
        }
    };

    const handleAssetChange = (id, field, value) => {
        setAssets((prev) =>
            prev.map((asset) =>
                asset.id === id ? { ...asset, [field]: value } : asset
            )
        );
    };

    const handleAddAsset = () => {
        setAssets((prev) => [...prev, { ...initialAsset, id: nextId }]);
        setNextId((id) => id + 1);
    };

    const handleRemoveAsset = (id) => {
        setAssets((prev) => prev.filter((asset) => asset.id !== id));
    };

    const handleSave = async () => {
        const employeeId = selectedEmployee ? selectedEmployee._id : null;
        // Add inventoryId and ensure price is set from product or asset
        const assetDetails = assets.map(asset => {
            const product = inventory.find(inv => inv.productName === asset.productName);
            return {
                ...asset,
                inventoryId: product ? product._id : null,
                price: product ? product.price : asset.price,
            };
        });
        const formData = { employeeId, assetDetails };
        setSaving(true);
        setSaveError(null);
        try {
            const response = await postRequest("/api/assets", formData);
            // Optionally handle response, e.g., show success message
            onRequestClose();
        } catch (err) {
            setSaveError("Failed to save asset(s). Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalWrapper title="Asset" open={open} onRequestClose={onRequestClose}>
            <div className="flex flex-col gap-4">
                <div className="max-h-[60vh] overflow-y-auto px-2">
                    <div className="relative">
                        <Input
                            placeholder="Search Employee"
                            className="w-full"
                            value={selectedEmployee ? selectedEmployee.name : employeeSearch}
                            onChange={e => {
                                setEmployeeSearch(e.target.value);
                                setShowEmployeeList(true);
                                setSelectedEmployee(null);
                            }}
                            onFocus={() => setShowEmployeeList(true)}
                            ref={searchInputRef}
                        />
                        {/* Employee List Dropdown */}
                        {(showEmployeeList && (employeeSearch.length > 0 || document.activeElement === searchInputRef.current)) && (
                            <div
                                ref={listRef}
                                className="absolute z-10 w-full bg-white border border-gray-200 rounded shadow mt-1 max-h-56 overflow-y-auto"
                            >
                                <Spin spinning={loadingEmployees}>
                                    <List
                                        bordered={false}
                                        size="small"
                                        dataSource={employees.filter(emp =>
                                            emp.name.toLowerCase().includes(employeeSearch.toLowerCase())
                                        )}
                                        renderItem={item => (
                                            <List.Item
                                                className="cursor-pointer hover:bg-gray-100"
                                                onClick={() => {
                                                    setSelectedEmployee(item);
                                                    setEmployeeSearch("");
                                                    setShowEmployeeList(false);
                                                }}
                                            >
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{item.name}</span>
                                                    <span className="text-xs text-gray-500">Department: {item.department || '-'}</span>
                                                    <span className="text-xs text-gray-500">Designation: {item.designation || '-'}</span>
                                                </div>
                                            </List.Item>
                                        )}
                                        locale={{ emptyText: employeeSearch ? 'No employee found' : 'Type to search employees' }}
                                    />
                                </Spin>
                            </div>
                        )}
                    </div>

                    <div className="mt-3">
                        <h3 className="font-medium mb-2 mt-3">Assets Details</h3>
                        {assets.map((asset, idx) => {
                            // Find selected product in inventory
                            const product = inventory.find(inv => inv.productName === asset.productName);
                            return (
                                <div
                                    key={asset.id}
                                    className="grid grid-cols-2 gap-4 mb-4 relative border border-gray-200 rounded p-4"
                                >
                                    <div>
                                        <label className="block mb-1">Product Name*</label>
                                        <Select
                                            showSearch
                                            placeholder="Select Product"
                                            value={asset.productName || undefined}
                                            loading={loadingInventory}
                                            onChange={value => {
                                                handleAssetChange(asset.id, "productName", value);
                                                // Reset serialNumber and price when product changes
                                                handleAssetChange(asset.id, "serialNumber", "");
                                                handleAssetChange(asset.id, "price", product ? product.price : "");
                                            }}
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().includes(input.toLowerCase())
                                            }
                                            className="w-full"
                                        >
                                            {inventory.map(inv => (
                                                <Select.Option key={inv._id} value={inv.productName}>
                                                    {inv.productName}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="block mb-1">Serial Number*</label>
                                        <Select
                                            placeholder="Select Serial Number"
                                            value={asset.serialNumber || undefined}
                                            onChange={value => handleAssetChange(asset.id, "serialNumber", value)}
                                            disabled={!product}
                                            className="w-full"
                                        >
                                            {product && product.serialNos.map(sn => (
                                                <Select.Option key={sn} value={sn}>{sn}</Select.Option>
                                            ))}
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="block mb-1">Price*</label>
                                        <Input
                                            prefix="RS"
                                            type="number"
                                            placeholder="0"
                                            value={product ? product.price : asset.price}
                                            disabled
                                            className="bg-gray-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-1">Assign Date*</label>
                                        <DatePicker
                                            className="w-full"
                                            value={asset.assignDate}
                                            onChange={(date) =>
                                                handleAssetChange(asset.id, "assignDate", date)
                                            }
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block mb-1">Description</label>
                                        <Input.TextArea
                                            placeholder="Write description about asset..."
                                            rows={2}
                                            value={asset.description}
                                            onChange={(e) =>
                                                handleAssetChange(asset.id, "description", e.target.value)
                                            }
                                        />
                                    </div>
                                    {assets.length > 1 && (
                                        <Tooltip title="Delete">
                                            <button
                                                type="button"
                                                className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                                                onClick={() => handleRemoveAsset(asset.id)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                            );
                        })}
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            className="w-full mt-2"
                            onClick={handleAddAsset}
                        >
                            Add More
                        </Button>
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                    <Button onClick={onRequestClose} disabled={saving}>Cancel</Button>
                    <Button
                        type="primary"
                        onClick={handleSave}
                        className="bg-primary hover:bg-primary/80"
                        loading={saving}
                        disabled={saving}
                    >
                        Save
                    </Button>
                </div>
                {saveError && <div className="text-red-500 text-sm mt-2">{saveError}</div>}
            </div>
        </ModalWrapper>
    );
};

export default AssetModal;